import blue from "@material-ui/core/colors/blue";
import { SnackbarOrigin } from "@material-ui/core/Snackbar";
import { Theme } from "@material-ui/core/styles";
import createMuiTheme from "@material-ui/core/styles/createMuiTheme";
import createPalette from "@material-ui/core/styles/createPalette";
import createTypography from "@material-ui/core/styles/createTypography";
import { OptionsObject } from "notistack";
import { Reducer } from "redux";
import { getUserPreferences, setUserPreferences } from "../auth";
import { SnackbarVariants } from "../components/snackbars";
import { getClientConfiguration } from "../configuration";

export interface AppState {
  theme: Theme;
  appFrame: {
    height: number;
    contentHeight: number;
  };
  snackbarMessage?: {
    message: string | React.ReactNode;
    messageKey: string | number;
    messageOptions: {
      variant?: SnackbarVariants;
      anchorOrigin?: SnackbarOrigin;
      autoHideDuration?: number | null;
    };
  };
  snackbarNotifications: Array<{
    message: string | React.ReactNode;
    messageKey: string | number;
    messageOptions: OptionsObject;
    messageDismissed: boolean;
  }>;
}

interface AppThemeUpdateAction {
  type: "APP_THEME_UPDATE";
  theme: Theme;
}

interface AppFrameUpdateAction {
  type: "APP_FRAME_UPDATE";
  height: number;
  contentHeight: number;
}

interface AppPostSnackbarMessageAction {
  type: "APP_POST_SNACKBAR_MESSAGE";
  message: string | React.ReactNode;
  messageKey: string | number;
  messageOptions: {
    variant?: SnackbarVariants;
    anchorOrigin?: SnackbarOrigin;
    autoHideDuration?: number | null;
  };
}

interface AppPostSnackbarNotificationAction {
  type: "APP_POST_SNACKBAR_NOTIFICATION";
  message: string | React.ReactNode;
  messageKey: string | number;
  messageOptions: OptionsObject;
}

interface AppCloseAllSnackbarNotificationsAction {
  type: "APP_CLOSE_ALL_SNACKBAR_NOTIFICATIONS";
  messageKeyRegexPattern?: string;
}

interface AppCloseSnackbarNotificationAction {
  type: "APP_CLOSE_SNACKBAR_NOTIFICATION";
  messageKey: string | number;
}

interface AppRemoveSnackbarNotificationAction {
  type: "APP_REMOVE_SNACKBAR_NOTIFICATION";
  messageKey: string | number;
}

type KnownAction =
  | AppThemeUpdateAction
  | AppFrameUpdateAction
  | AppPostSnackbarMessageAction
  | AppPostSnackbarNotificationAction
  | AppCloseAllSnackbarNotificationsAction
  | AppCloseSnackbarNotificationAction
  | AppRemoveSnackbarNotificationAction;

export const actionCreators = {
  updateTheme: (themeName: string | undefined) => {
    if (themeName) {
      setUserPreferences({ theme: themeName });
    }
    return {
      type: "APP_THEME_UPDATE",
      theme: themeName && createTheme(themeName === "dark" ? "dark" : "light"),
    };
  },

  updateHeight: (height: number, contentHeight: number) => ({
    type: "APP_FRAME_UPDATE",
    height,
    contentHeight,
  }),

  postSnackbarMessage: (
    message: string | React.ReactNode,
    options?: {
      variant?: SnackbarVariants;
      anchorOrigin?: SnackbarOrigin;
      autoHideDuration?: number | null;
    },
  ) => {
    const messageKey = new Date().getTime() + Math.random();
    const messageOptions = { ...(options || {}) };

    return {
      type: "APP_POST_SNACKBAR_MESSAGE",
      message,
      messageKey,
      messageOptions,
    };
  },

  postSnackbarNotification: (message: string | React.ReactNode, options?: OptionsObject) => {
    const messageKey = (options && options.key) || new Date().getTime() + Math.random();
    const messageOptions = { ...(options || {}) };

    return {
      type: "APP_POST_SNACKBAR_NOTIFICATION",
      message,
      messageKey,
      messageOptions,
    };
  },

  closeAllSnackbarNotifications: (messageKeyRegexPattern?: string) => ({
    type: "APP_CLOSE_ALL_SNACKBAR_NOTIFICATIONS",
    messageKeyRegexPattern,
  }),

  closeSnackbarNotification: (messageKey: string | number) => ({
    type: "APP_CLOSE_SNACKBAR_NOTIFICATION",
    messageKey,
  }),

  removeSnackbarNotification: (messageKey: string | number) => ({
    type: "APP_REMOVE_SNACKBAR_NOTIFICATION",
    messageKey,
  }),
};

const createTheme = (themeName?: "light" | "dark") => {
  const clientConfiguration = getClientConfiguration();
  const uiTheme = clientConfiguration && clientConfiguration.ui && clientConfiguration.ui.theme;

  // If the theme type was not specified, try to source it from User Preferences in local storage (if available)...
  if (!themeName) {
    const userPreferences = getUserPreferences();
    themeName =
      userPreferences && (userPreferences as any).theme
        ? ((userPreferences as any).theme as "light" | "dark")
        : (uiTheme && uiTheme.type) || "light";
  }

  const palette = createPalette({
    type: themeName,
    primary: {
      main: (uiTheme && uiTheme.colours && uiTheme.colours.primary) || blue[800],
    },
    secondary: {
      main: (uiTheme && uiTheme.colours && uiTheme.colours.secondary) || blue.A700,
    },
  });

  const theme = createMuiTheme({
    palette,
    typography: createTypography(palette, {
      fontFamily: "inherit",
      allVariants: {
        lineHeight: 1.35,
      },
    }),
  });

  return theme;
};

const initialState = (): AppState => ({
  theme: createTheme(),
  appFrame: {
    height: 1080,
    contentHeight: 1080,
  },
  snackbarNotifications: [],
});

export const reducer: Reducer<AppState, KnownAction> = (state, action) => {
  state = state || initialState();

  switch (action.type) {
    case "APP_THEME_UPDATE":
      state = {
        ...state,
        theme: action.theme || state.theme,
      };
      break;
    case "APP_FRAME_UPDATE":
      state = {
        ...state,
        appFrame: {
          height: action.height,
          contentHeight: action.contentHeight,
        },
      };
      break;
    case "APP_POST_SNACKBAR_MESSAGE":
      state = {
        ...state,
        snackbarMessage: {
          message: action.message,
          messageKey: action.messageKey,
          messageOptions: action.messageOptions,
        },
      };
      break;
    case "APP_POST_SNACKBAR_NOTIFICATION":
      state = {
        ...state,
        snackbarNotifications: [
          ...state.snackbarNotifications,
          {
            message: action.message,
            messageKey: action.messageKey,
            messageOptions: action.messageOptions,
            messageDismissed: false,
          },
        ],
      };
      break;
    case "APP_CLOSE_ALL_SNACKBAR_NOTIFICATIONS":
      const messageKeyRegex = action.messageKeyRegexPattern ? new RegExp(action.messageKeyRegexPattern) : undefined;
      state = {
        ...state,
        snackbarNotifications: state.snackbarNotifications.map(snackbarNotification =>
          (messageKeyRegex
          ? messageKeyRegex.test(`${snackbarNotification.messageKey}`)
          : true)
            ? {
                ...snackbarNotification,
                messageDismissed: true,
              }
            : snackbarNotification,
        ),
      };
      break;
    case "APP_CLOSE_SNACKBAR_NOTIFICATION":
      state = {
        ...state,
        snackbarNotifications: state.snackbarNotifications.map(snackbarNotification =>
          snackbarNotification.messageKey === action.messageKey
            ? { ...snackbarNotification, messageDismissed: true }
            : snackbarNotification,
        ),
      };
      break;
    case "APP_REMOVE_SNACKBAR_NOTIFICATION":
      state = {
        ...state,
        snackbarNotifications: state.snackbarNotifications.filter(
          snackbarNotification => snackbarNotification.messageKey !== action.messageKey,
        ),
      };
      break;
    default:
  }

  return state;
};
